import { AppstoreOutlined, DownOutlined, PlusOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { Button, Dropdown, Input, Menu, Tooltip } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import AppPageHeader from '../../components/AppPageHeader'
import DocumentsLayout from '../../layouts/DocumentsLayout'
import CreateFolderModal from './CreateFolderModal'
import MoveDocumentModal from './MoveDocumentModal'
import MyDocumentGridView from './MyDocumentGridView'
import MyDocumentTableView from './MyDocumentTableView'
import UploadFileModal from './UploadFileModal'
import SelectPeoplePopup from '../../components/SelectPeoplePopup'
import { ASSIGNEE_TYPE, ACL_ACTION_TYPE } from '../../constants'
import AccessControlAction from '../../components/AccessControlAction'
import { message } from 'antd'

const { Search } = Input

const MyDocumentPage = props => {
  const { myDocumentStore, loadingAnimationStore, selectPeopleStore } = props
  const [viewMode, setViewMode] = useState('list')
  const [createFolderVisible, setCreateFolderVisible] = useState(false)
  const [uploadFileVisible, setUploadFileVisible] = useState(false)
  const [moveDocumentVisible, setMoveDocumentVisible] = useState(false)
  const [shareDocumentVisible, setShareDocumentVisible] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState(null)

  useEffect(() => {
    fetchData()
  }, [myDocumentStore.currentParentId])

  const fetchData = async () => {
    loadingAnimationStore.showSpinner(true)
    try {
      await myDocumentStore.getList()
      if (!myDocumentStore.currentParentId) {
        await myDocumentStore.getSharedList()
      }
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleSearch = value => {
    myDocumentStore.setQueryParams({ keyword: value })
    myDocumentStore.setSharedQueryParams({ keyword: value })
    fetchData()
  }



  const menuAdd = (
    <Menu>
      <Menu.Item key="folder" onClick={() => setCreateFolderVisible(true)}>
        Tạo thư mục
      </Menu.Item>
      <Menu.Item key="file" onClick={() => setUploadFileVisible(true)}>
        Tải file
      </Menu.Item>
    </Menu>
  )

  const openMoveModal = doc => {
    setSelectedDocument(doc)
    setMoveDocumentVisible(true)
  }

  const openShareModal = doc => {
    setSelectedDocument(doc)
    selectPeopleStore.setAssigneePopupType(ASSIGNEE_TYPE.USER)
    selectPeopleStore.selectPopupName = 'Chọn người chia sẻ'
    
    // Set current selected users if any
    const initialUsers = (doc.sharedWith || []).map(u => ({
      id: u.username || u.id || u,
      username: u.username || u.id || u,
      full_name: u.fullName || u.username || u,
      name: u.fullName || u.username || u,
    }))
    selectPeopleStore.selectUserData[ASSIGNEE_TYPE.USER] = initialUsers
    
    setShareDocumentVisible(true)
  }

  const handleShareSubmit = async () => {
    try {
      loadingAnimationStore.showSpinner(true)
      const selectedUsersData = selectPeopleStore.selectUserData[ASSIGNEE_TYPE.USER] || []
      const sharedWith = selectedUsersData.map(u => u.username || u.id)
      
      await myDocumentStore.shareDocument(selectedDocument.id, { sharedWith })
      message.success('Chia sẻ tài liệu thành công')
      setShareDocumentVisible(false)
      fetchData()
    } catch (error) {
      message.error('Lỗi khi chia sẻ tài liệu')
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  return (
    <DocumentsLayout>
      <Helmet>
        <title>Tài liệu</title>
      </Helmet>
      
      <AppPageHeader
        title="Tài liệu"
        addon={
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <Search
              placeholder="Tìm kiếm thư mục, tài liệu..."
              allowClear
              onSearch={handleSearch}
              style={{ width: 250 }}
            />
            <AccessControlAction aclActionType={ACL_ACTION_TYPE.create__MY_DOCUMENT}>
              <Dropdown overlay={menuAdd} trigger={['click']}>
                <Button type="primary">
                  <PlusOutlined style={{ marginRight: 5 }} />
                  THÊM MỚI
                </Button>
              </Dropdown>
            </AccessControlAction>
          </div>
        }
      />
      
      <div style={{ padding: '0 24px' }}>

        {viewMode === 'list' ? (
          <MyDocumentTableView 
            store={myDocumentStore} 
            onMove={openMoveModal} 
            onShare={openShareModal}
            fetchData={fetchData} 
          />
        ) : (
          <MyDocumentGridView 
            store={myDocumentStore} 
            onMove={openMoveModal} 
            onShare={openShareModal}
            fetchData={fetchData} 
          />
        )}
      </div>

      <CreateFolderModal 
        visible={createFolderVisible} 
        onCancel={() => setCreateFolderVisible(false)} 
        onSuccess={() => { setCreateFolderVisible(false); fetchData(); }} 
        parentId={myDocumentStore.currentParentId} 
      />
      <UploadFileModal 
        visible={uploadFileVisible} 
        onCancel={() => setUploadFileVisible(false)} 
        onSuccess={() => { setUploadFileVisible(false); fetchData(); }} 
        parentId={myDocumentStore.currentParentId} 
      />
      <MoveDocumentModal 
        visible={moveDocumentVisible} 
        document={selectedDocument}
        onCancel={() => setMoveDocumentVisible(false)} 
        onSuccess={() => { setMoveDocumentVisible(false); fetchData(); }} 
      />
      <SelectPeoplePopup
        isVisibleSelectPeoplePopup={shareDocumentVisible}
        handleCancelSelectUser={() => setShareDocumentVisible(false)}
        handleSubmitSelectUser={handleShareSubmit}
      />
    </DocumentsLayout>
  )
}

export default memo(inject('myDocumentStore', 'loadingAnimationStore', 'selectPeopleStore')(observer(MyDocumentPage)))
